import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/fr';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { Calendar as CalendarIcon, MapPin, Euro, Notebook, Film } from 'lucide-react';

// Configure moment to use French locale
moment.locale('fr');
const localizer = momentLocalizer(moment);

const CalendarView = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    loadEvents(currentDate);
  }, [currentDate]);

  const loadEvents = async (date) => {
    setLoading(true);
    try {
      // Calculate the start and end dates for the view
      const start = moment(date).startOf('month').subtract(1, 'week').format('YYYY-MM-DD');
      const end = moment(date).endOf('month').add(1, 'week').format('YYYY-MM-DD');

      const res = await axios.get(`${API}/calendar/events`, { 
        params: { start, end },
        withCredentials: true 
      });
      
      const formattedEvents = res.data.map(event => ({
        ...event, // Keep all original properties (like color)
        start: new Date(event.start),
        end: new Date(event.start), // For single-day events
        allDay: true // Assume all are full-day events
      }));
      
      setEvents(formattedEvents);
    } catch (error) {
      console.error('Error loading calendar events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event.resource);
  };

  const handleNavigate = (eventId) => {
    navigate(`/backoffice/finances/${eventId}`);
  };

  // Custom event style
  const eventStyleGetter = (event) => {
    const style = {
      backgroundColor: event.color || '#d97706', // Use color from the event root
      borderRadius: '5px',
      opacity: 0.8,
      color: 'white',
      border: '0px',
      display: 'block'
    };
    return { style };
  };

  const messages = {
    allDay: 'Journée',
    previous: 'Précédent',
    next: 'Suivant',
    today: "Aujourd'hui",
    month: 'Mois',
    week: 'Semaine',
    day: 'Jour',
    agenda: 'Agenda',
    date: 'Date',
    time: 'Heure',
    event: 'Événement',
    noEventsInRange: 'Aucun événement dans cette période',
    showMore: total => `+ ${total} événement(s) supplémentaire(s)`
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-stone-500">Chargement du calendrier...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-stone-900 flex items-center gap-2">
          <CalendarIcon className="h-8 w-8 text-amber-600" />
          Calendrier des Événements
        </h1>
        <p className="text-stone-600 mt-1">Visualisez tous vos événements à venir</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-2">
          <Card>
            <CardContent className="p-4">
              <div style={{ height: '600px' }}>
                <Calendar
                  localizer={localizer}
                  events={events}
                  startAccessor="start"
                  endAccessor="end"
                  style={{ height: '100%' }}
                  onSelectEvent={handleSelectEvent}
                  onNavigate={(date) => setCurrentDate(date)}
                  date={currentDate} // Control the displayed date
                  eventPropGetter={eventStyleGetter}
                  messages={messages}
                  views={['month', 'week', 'day', 'agenda']}
                  defaultView="month"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Event Details Sidebar */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Détails de l'événement</CardTitle>
            </CardHeader>
            <CardContent>
              {selectedEvent ? (
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-lg text-stone-900 mb-2">
                      {selectedEvent.prestation}
                    </h3>
                    <Badge className="bg-amber-100 text-amber-700 capitalize">
                      {selectedEvent.statut?.replace('_', ' ') || 'Planifié'}
                    </Badge>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <CalendarIcon className="h-4 w-4 text-stone-500 mt-1" />
                      <div>
                        <p className="text-sm font-medium text-stone-700">Date</p>
                        <p className="text-sm text-stone-600">
                          {moment(selectedEvent.start).format('dddd D MMMM YYYY')}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <span className="text-lg mt-0.5">👤</span>
                      <div>
                        <p className="text-sm font-medium text-stone-700">Client</p>
                        <p className="text-sm text-stone-600">{selectedEvent.client}</p>
                      </div>
                    </div>

                    {selectedEvent.lieu && (
                      <div className="flex items-start gap-2">
                        <MapPin className="h-4 w-4 text-stone-500 mt-1" />
                        <div>
                          <p className="text-sm font-medium text-stone-700">Lieu</p>
                          <p className="text-sm text-stone-600">{selectedEvent.lieu}</p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-start gap-2">
                      <Euro className="h-4 w-4 text-stone-500 mt-1" />
                      <div>
                        <p className="text-sm font-medium text-stone-700">Montant</p>
                        <p className="text-sm font-semibold text-green-600">
                          {selectedEvent.montant?.toLocaleString('fr-FR')}€
                        </p>
                      </div>
                    </div>

                    {selectedEvent.rushes_delivered_at && (
                      <div className="flex items-start gap-2 pt-2 border-t">
                        <Film className="h-4 w-4 text-stone-500 mt-1" />
                        <div>
                          <p className="text-sm font-medium text-stone-700">Rushes Livrés le</p>
                          <p className="text-sm text-stone-600">
                            {moment(selectedEvent.rushes_delivered_at).format('D MMMM YYYY')}
                          </p>
                        </div>
                      </div>
                    )}

                    {selectedEvent.note && (
                      <div className="flex items-start gap-2 pt-2 border-t">
                        <Notebook className="h-4 w-4 text-stone-500 mt-1" />
                        <div>
                          <p className="text-sm font-medium text-stone-700">Note</p>
                          <p className="text-sm text-stone-600 whitespace-pre-wrap">{selectedEvent.note}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {selectedEvent.type === 'evenement' ? (
                    <button
                      onClick={() => handleNavigate(selectedEvent.id)}
                      className="w-full mt-4 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      Voir les détails financiers
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate(`/backoffice/devis/${selectedEvent.id}/edit`)}
                      className="w-full mt-4 bg-stone-600 hover:bg-stone-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      Voir le devis
                    </button>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-stone-500">
                  <CalendarIcon className="h-12 w-12 mx-auto mb-2 text-stone-300" />
                  <p className="text-sm">Sélectionnez un événement pour voir les détails</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Stats */}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="text-lg">Statistiques</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-stone-600">Total événements</span>
                  <span className="font-semibold text-stone-900">{events.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-stone-600">Ce mois</span>
                  <span className="font-semibold text-amber-600">
                    {events.filter(e => moment(e.start).isSame(moment(), 'month')).length}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;
